import { z } from 'zod';
import { PrioritySchema, PhaseSchema } from './template.js';
// Card attachment validation
export const CardAttachmentSchema = z.object({
    id: z.string().min(1),
    s3Key: z.string().min(1).max(500),
    filename: z.string().min(1).max(255),
    contentType: z.enum(['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic']),
    size: z.number().int().min(1).max(10 * 1024 * 1024), // 10 MB max
    uploadedAt: z.string().datetime(),
    uploadedBy: z.string().min(1),
});
// Column status validation (free-form slugs)
export const ColumnStatusSchema = z.string().min(1).max(50);
// Card status validation (free-form — matches column phase name)
export const CardStatusSchema = z.string().min(1).max(50);
// Activity type validation
export const ActivityTypeSchema = z.enum([
    'analysis',
    'planning',
    'coding',
    'testing',
    'review',
    'deployment',
    'feedback',
    'error',
    'status-update',
    'question',
    'migration',
    'escalation'
]);
// Agent question validation
export const QuestionStatusSchema = z.enum(['open', 'answered']);
export const AgentQuestionSchema = z.object({
    id: z.string().min(1),
    agentId: z.string().min(1),
    phase: PhaseSchema,
    question: z.string().min(1).max(2000),
    context: z.string().max(5000).optional(),
    answer: z.string().max(5000).optional(),
    answeredBy: z.string().optional(),
    answeredAt: z.string().datetime().optional(),
    status: QuestionStatusSchema,
    createdAt: z.string().datetime(),
});
// Pending prompt validation
export const PendingPromptSchema = z.object({
    id: z.string().min(1),
    prompt: z.string().min(1).max(10000),
    agentRoleId: z.string().min(1),
    createdBy: z.string().min(1),
    createdAt: z.string().datetime(),
});
// PR history entry — archived past PR for a card
export const PullRequestHistoryEntrySchema = z.object({
    number: z.number().int().positive().optional(),
    url: z.string().max(500).optional(),
    branch: z.string().max(255).optional(),
    merged: z.boolean().optional(),
    archivedAt: z.string().datetime(),
});
// Card metadata validation
export const CardMetadataSchema = z.object({
    estimatedHours: z.number().min(0).max(1000).optional(),
    actualHours: z.number().min(0).max(1000).optional(),
    githubBranch: z.string().max(255).optional(),
    githubPR: z.string().max(255).optional(),
    prUrl: z.string().max(500).optional(),
    costEstimate: z.number().min(0).max(1000).optional(),
    actualCost: z.number().min(0).max(1000).optional(),
    tags: z.array(z.string().max(50)).max(20),
    plan: z.string().max(50000).optional(),
    questions: z.array(AgentQuestionSchema).optional(),
    inputRequired: z.boolean().optional(),
    needsReeval: z.boolean().optional(),
    reevalReason: z.string().optional(),
    rebaseConflict: z.boolean().optional(),
    phaseRetryCount: z.number().min(0).optional(),
    failureCount: z.number().min(0).optional(),
    lastSessionId: z.string().max(200).nullable().optional(),
    attachments: z.array(CardAttachmentSchema).max(10).optional(),
    pendingPrompt: PendingPromptSchema.optional(),
    prHistory: z.array(PullRequestHistoryEntrySchema).max(50).optional(),
    blockedBy: z.array(z.string().min(1).max(100)).max(20).optional(),
});
// Runner settings validation
export const RunnerSettingsSchema = z.object({
    cliCommand: z.string().max(2000).optional(),
    claudeArgs: z.string().max(2000).optional(),
    autoStart: z.boolean().optional(),
    claudeEnv: z.record(z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/).max(100), z.string().max(2000)).refine(obj => Object.keys(obj).length <= 20, { message: 'Maximum 20 environment variables' }).optional(),
    status: z.enum(['running', 'stopped', 'error']).optional(),
    desiredState: z.enum(['running', 'stopped']).optional(),
    runnerId: z.string().max(200).optional(),
    runnerStartedAt: z.string().optional(),
});
// Board settings validation
export const BoardSettingsSchema = z.object({
    autoAssignments: z.boolean(),
    requiredApprovals: z.number().min(0).max(10),
    defaultTemplate: z.string().optional(),
    runner: RunnerSettingsSchema.optional(),
    integrations: z.object({
        github: z.object({
            owner: z.string().min(1).max(100),
            repo: z.string().min(1).max(100),
            tokenId: z.string().max(50).optional(),
            token: z.string().optional(),
            defaultBranch: z.string().min(1).max(100),
            autoMerge: z.boolean(),
            requireReviews: z.boolean(),
            trackWorkflows: z.boolean()
        }).optional(),
        slack: z.object({
            webhook: z.string().url(),
            channel: z.string().min(1).max(100),
            mentions: z.array(z.string())
        }).optional(),
        discord: z.object({
            webhook: z.string().url(),
            channel: z.string().min(1).max(100),
            mentions: z.array(z.string())
        }).optional()
    }),
    notifications: z.object({
        email: z.boolean(),
        slack: z.boolean(),
        discord: z.boolean(),
        events: z.array(z.enum([
            'card-created',
            'card-moved',
            'plan-ready',
            'code-ready',
            'agent-error'
        ]))
    }),
    color: z.string().max(32).optional(),
});
// GitHub token entry validation
export const GitHubTokenEntrySchema = z.object({
    id: z.string().min(1).max(50),
    label: z.string().min(1).max(100),
    secretName: z.string().optional(),
    token: z.string().optional(), // legacy
    username: z.string().min(1),
    avatarUrl: z.string(),
}).refine(data => data.secretName || data.token, { message: 'Either secretName or token required' });
// Feature flags validation
export const FeatureFlagsSchema = z.object({
    autoTriage: z.boolean(),
    autoImplementation: z.boolean(),
    autoDeployment: z.boolean(),
    realTimeUpdates: z.boolean(),
    costTracking: z.boolean(),
    auditLogging: z.boolean()
});
// App settings validation
export const AppSettingsSchema = z.object({
    githubToken: z.string().min(1),
    defaultTemplateId: z.string().min(1),
    maxConcurrentCards: z.number().min(1).max(100),
    features: FeatureFlagsSchema
});
// Column validation
export const ColumnSchema = z.object({
    id: z.string().min(1),
    boardId: z.string().min(1),
    name: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    order: z.number().min(0).max(100),
    status: ColumnStatusSchema,
    autoTransitions: z.array(z.object({
        condition: z.string().min(1),
        targetColumnId: z.string().min(1),
        delay: z.number().min(0).max(60 * 24 * 7).optional() // 1 week max delay
    })).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
// Card validation
export const CardSchema = z.object({
    id: z.string().min(1),
    boardId: z.string().min(1),
    columnId: z.string().min(1),
    title: z.string().max(200),
    description: z.string().max(5000),
    status: CardStatusSchema,
    priority: PrioritySchema,
    order: z.number().min(0),
    assignedAgents: z.array(z.string().min(1)).max(10),
    templateId: z.string().min(1).optional(),
    metadata: CardMetadataSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    createdBy: z.string().min(1)
});
// Board validation
export const BoardSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1).max(100),
    description: z.string().max(1000).optional(),
    columns: z.array(ColumnSchema),
    settings: BoardSettingsSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    createdBy: z.string().min(1)
});
// Activity validation
export const ActivitySchema = z.object({
    id: z.string().min(1),
    cardId: z.string().min(1),
    agentId: z.string().min(1),
    type: ActivityTypeSchema,
    phase: PhaseSchema,
    title: z.string().min(1).max(200),
    description: z.string().min(1).max(2000),
    metadata: z.object({
        tokenUsage: z.object({
            input: z.number().min(0),
            output: z.number().min(0),
            total: z.number().min(0)
        }).optional(),
        artifacts: z.array(z.object({
            id: z.string().min(1),
            type: z.enum(['code', 'test', 'documentation', 'plan', 'diff', 'screenshot', 'log']),
            name: z.string().min(1).max(200),
            content: z.string().optional(),
            url: z.string().url().optional(),
            metadata: z.record(z.any()),
            createdAt: z.string().datetime()
        })).optional(),
        statusChange: z.object({
            from: z.string(),
            to: z.string(),
            reason: z.string().min(1).max(500),
            confidence: z.number().min(0).max(1)
        }).optional(),
        error: z.object({
            type: z.string().min(1),
            message: z.string().min(1),
            code: z.string().optional(),
            stack: z.string().optional(),
            context: z.record(z.any()).optional()
        }).optional()
    }),
    createdAt: z.string().datetime()
});
// Partial schemas for updates
export const BoardUpdateSchema = BoardSchema.partial().omit({
    id: true,
    createdAt: true,
    createdBy: true
});
export const ColumnUpdateSchema = ColumnSchema.partial().omit({
    id: true,
    boardId: true,
    createdAt: true
});
export const CardUpdateSchema = CardSchema.partial().omit({
    id: true,
    createdAt: true,
    createdBy: true
});
export const CardCreateSchema = CardSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true
});
// Input validation schemas (for API requests)
export const CreateBoardInputSchema = z.object({
    name: z.string().min(1).max(100),
    description: z.string().max(1000).optional(),
    templateId: z.string().min(1)
});
export const CreateCardInputSchema = z.object({
    title: z.string().max(200).optional().default(''),
    description: z.string().max(5000).optional().default(''),
    columnId: z.string().min(1).optional(), // defaults to backlog
});
export const MoveCardInputSchema = z.object({
    columnId: z.string().min(1),
    order: z.number().min(0).optional()
});
export const UpdateCardInputSchema = z.object({
    title: z.string().max(200).optional(),
    description: z.string().max(5000).optional(),
    tags: z.array(z.string().max(50)).max(20).optional(),
    plan: z.string().max(50000).optional(),
    metadata: z.record(z.unknown()).optional(),
    priority: PrioritySchema.optional(),
});
// Query validation schemas
export const ListCardsQuerySchema = z.object({
    boardId: z.string().min(1).optional(),
    columnId: z.string().min(1).optional(),
    status: z.string().optional(),
    priority: PrioritySchema.optional(),
    assignedAgent: z.string().min(1).optional(),
    limit: z.number().min(1).max(100).optional().default(50),
    offset: z.number().min(0).optional().default(0),
    sortBy: z.enum(['createdAt', 'updatedAt', 'priority', 'order']).optional().default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).optional().default('desc')
});
export const ListActivitiesQuerySchema = z.object({
    cardId: z.string().min(1).optional(),
    agentId: z.string().min(1).optional(),
    type: ActivityTypeSchema.optional(),
    phase: z.string().optional(),
    limit: z.number().min(1).max(100).optional().default(50),
    offset: z.number().min(0).optional().default(0)
});
// Validation helper functions
export function validateBoard(board) {
    return BoardSchema.parse(board);
}
export function validateColumn(column) {
    return ColumnSchema.parse(column);
}
export function validateCard(card) {
    return CardSchema.parse(card);
}
export function validateActivity(activity) {
    return ActivitySchema.parse(activity);
}
export function validateAppSettings(settings) {
    return AppSettingsSchema.parse(settings);
}
/**
 * Validate whether a card can be moved from one column to another.
 *
 * Rules:
 * - Same column (reorder): ALWAYS allowed → 'same-column'
 * - Backward (any previous column): ALWAYS allowed → 'valid'
 * - Forward one step from a manual phase: ALLOWED → 'valid'
 * - Forward from an auto-advance phase (autoAdvance=true AND agents.length > 0): BLOCKED
 * - Forward more than one step: ALWAYS blocked
 *
 * `workflow` is optional — without it every phase is treated as manual.
 */
export function validateColumnMove({ columns, sourceColumnId, targetColumnId, workflow, }) {
    if (sourceColumnId === targetColumnId)
        return 'same-column';
    const sorted = [...columns].sort((a, b) => a.order - b.order);
    const sourceIdx = sorted.findIndex((c) => c.id === sourceColumnId);
    const targetIdx = sorted.findIndex((c) => c.id === targetColumnId);
    if (sourceIdx === -1 || targetIdx === -1)
        return 'blocked';
    // Backward move — always allowed
    if (targetIdx < sourceIdx)
        return 'valid';
    // Forward move — check if source is auto-advance
    const sourceCol = sorted[sourceIdx];
    const sourcePhase = workflow?.find((p) => p.name === sourceCol.status);
    // Disabled phases are treated as pass-through (like manual phases)
    const isDisabled = sourcePhase?.disabled;
    const isAuto = !isDisabled && workflow
        ? workflow.some((p) => p.name === sourceCol.status && p.autoAdvance && p.agents.length > 0)
        : false;
    if (isAuto)
        return 'blocked';
    // Manual phase — only allow one step forward
    if (targetIdx === sourceIdx + 1)
        return 'valid';
    return 'blocked';
}
/**
 * Prerequisite cards must reach a "done" column before a dependent card
 * may advance. Returns the list of blocker card IDs that are NOT yet in a
 * done-status column. A blocker whose card cannot be found is treated as
 * unresolved (safer default: surface it rather than silently dropping it).
 *
 * DONE_COLUMN_STATUS — we treat any column with status === 'done' as the
 * terminal state. This matches the convention used across the codebase
 * (see KanbanBoard.tsx, runner poll filters). Callers may pass a custom
 * set of done-statuses (e.g. workflow phases flagged terminal).
 */
const DEFAULT_DONE_STATUSES = new Set(['done']);
export function getUnresolvedBlockers(card, allCards, columns, doneStatuses = DEFAULT_DONE_STATUSES) {
    const blockedBy = card.metadata?.blockedBy;
    if (!blockedBy || blockedBy.length === 0)
        return [];
    const cardById = new Map(allCards.map(c => [c.id, c]));
    const statusByColumnId = new Map(columns.map(col => [col.id, col.status]));
    const unresolved = [];
    for (const blockerId of blockedBy) {
        const blocker = cardById.get(blockerId);
        if (!blocker) {
            // Missing blocker — conservatively treat as unresolved
            unresolved.push(blockerId);
            continue;
        }
        const status = statusByColumnId.get(blocker.columnId);
        if (!status || !doneStatuses.has(status)) {
            unresolved.push(blockerId);
        }
    }
    return unresolved;
}
export function isCardBlocked(card, allCards, columns, doneStatuses) {
    return getUnresolvedBlockers(card, allCards, columns, doneStatuses).length > 0;
}
export function validateCardOrder(cards, newCard, targetColumnId) {
    const cardsInColumn = cards
        .filter(card => card.columnId === targetColumnId)
        .filter(card => card.id !== newCard.id);
    // Check if order is unique within column
    return !cardsInColumn.some(card => card.order === newCard.order);
}
export function validateAgentAssignment(card, agentId, phase) {
    // Agent must be in the assigned agents list
    if (!card.assignedAgents.includes(agentId)) {
        return false;
    }
    // Agent must be authorized for the current phase
    // This would check against the team template in a real implementation
    return true;
}
export function sanitizeCardInput(input) {
    // Remove any sensitive or unwanted fields
    const sanitized = { ...input };
    delete sanitized.id;
    delete sanitized.createdAt;
    delete sanitized.updatedAt;
    delete sanitized.createdBy;
    // Sanitize description to prevent XSS
    if (typeof sanitized.description === 'string') {
        sanitized.description = sanitized.description
            .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
            .replace(/<[^>]+>/g, '')
            .trim();
    }
    return sanitized;
}
export function generateCardId() {
    return `card_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}
export function generateBoardId() {
    return `board_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}
export function generateColumnId() {
    return `col_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}
export function generateActivityId() {
    return `activity_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}
export function generateQuestionId() {
    return `question_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}
export function generatePromptId() {
    return `prompt_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}
// Input schemas for question endpoints
export const PostQuestionInputSchema = z.object({
    agentId: z.string().min(1),
    phase: PhaseSchema,
    question: z.string().min(1).max(2000),
    context: z.string().max(5000).optional(),
});
export const AnswerQuestionInputSchema = z.object({
    answer: z.string().min(1).max(5000),
});
// User prompt input schema
export const SubmitPromptInputSchema = z.object({
    prompt: z.string().min(1).max(10000),
    agentRoleId: z.string().min(1),
});
// Agent run input schemas
export const RunStatusSchema = z.enum([
    'queued', 'running', 'completed', 'failed', 'cancelled', 'timeout'
]);
export const CreateAgentRunInputSchema = z.object({
    id: z.string().min(1).optional(),
    cardId: z.string().min(1),
    agentRoleId: z.string().min(1),
    phase: PhaseSchema,
});
export const UpdateAgentRunInputSchema = z.object({
    status: RunStatusSchema.optional(),
    sessionId: z.string().max(200).optional(),
    completedAt: z.string().datetime().optional(),
    usage: z.object({
        tokens: z.object({
            input: z.number().min(0),
            output: z.number().min(0),
            total: z.number().min(0),
        }),
        costUSD: z.number().min(0),
        timeMinutes: z.number().min(0),
    }).optional(),
    output: z.object({
        summary: z.string().max(5000),
        artifacts: z.array(z.any()).optional(),
        nextActions: z.array(z.string()).optional(),
        recommendations: z.array(z.string()).optional(),
        statusChange: z.object({
            from: z.string(),
            to: z.string(),
            reason: z.string(),
            confidence: z.number().min(0).max(1),
        }).optional(),
    }).optional(),
    error: z.object({
        type: z.string().min(1),
        message: z.string().min(1),
        code: z.string().optional(),
        stack: z.string().optional(),
        context: z.record(z.any()).optional(),
    }).optional(),
});
//# sourceMappingURL=validation.js.map